import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PackageDetailsBasicInformationTab from '../components/package_details/PackageDetailBasicInformationTab';
import OrderInfo from '../components/zoe_orders/OrderInfo';
import zoeOrdersService from '../services/zoe-orders-service';

const formatDateTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', // EST
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short' // "EST"
  }).format(date);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const getOrderStatusColor = (status) => {
  if (!status) return 'default';

  switch (status.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'processing':
      return 'info';
    case 'shipped':
      return 'primary';
    case 'partially_shipped':
      return 'secondary';
    case 'delivered':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'returned':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusColor = (status) => {
  if (!status) return 'default';
  const lower = status.toLowerCase();

  if (lower.includes('created') || lower.includes('rmspocreated'))
    return 'info';
  if (lower.includes('shipped')) return 'primary';
  if (lower.includes('delivered')) return 'success';
  if (lower.includes('cancelled')) return 'error';
  if (lower.includes('pending')) return 'warning';

  return 'default';
};

export default function ZoeOrderDetailsPage() {
  const [orderDetails, setOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Modal state
  const [openEditModal, setOpenEditModal] = useState(false);
  const [formValues, setFormValues] = useState({});

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenEdit = () => {
    setFormValues(orderDetails); // preload current values
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await zoeOrdersService.updateZoeOrder(id, formValues);
      if (response.success || response.code === 0) {
        setOrderDetails(formValues);
        setOpenEditModal(false);
        setError(null);
      } else {
        setError(response.message || 'Update failed');
        console.error('Update error:', response.message);
      }
    } catch (error) {
      setError('Failed to update order');
      console.error('Save error:', error);
    }
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await zoeOrdersService.getZoeOrder(id);
        if (response.success || response.code === 0) {
          setOrderDetails(response.data);
        } else {
          setError(response.message || 'Failed to load order details');
          console.error('Error:', response.message);
        }
      } catch (error) {
        setError('Failed to load order details');
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error && !orderDetails.Id) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/zoe-orders')}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  const overviewItems = [
    { title: 'Order ID', value: orderDetails.Id },
    { title: 'Order Number', value: orderDetails.OrderNumber },
    {
      title: 'Order Status',
      value: orderDetails.OrderStatus,
      render: (value) => (
        <Chip
          label={value || 'Unknown'}
          color={getOrderStatusColor(value)}
          size="small"
        />
      )
    },
    {
      title: 'Status',
      value: orderDetails.Status,
      render: (value) =>
        value ? (
          <Chip
            label={value}
            color={getStatusColor(value)}
            variant="outlined"
            size="small"
          />
        ) : (
          '—'
        )
    },

    // Customer Information
    { title: 'Customer Name', value: orderDetails.CustomerName },
    { title: 'Customer Email', value: orderDetails.CustomerEmail },
    {
      title: 'Customer Account Number',
      value: orderDetails.CustomerAccountNumber
    },
    { title: 'Customer Code', value: orderDetails.CustomerCode },

    // Order Information
    { title: 'Order Type', value: orderDetails.OrderType },
    { title: 'Order Date', value: formatDateTime(orderDetails.OrderDate) },
    { title: 'Order Date Only', value: formatDate(orderDetails.OrderDateOnly) },
    { title: 'Reference ID', value: orderDetails.ReferenceId },

    // Legacy/External IDs
    { title: 'Old Zoe Order ID', value: orderDetails.OldZoeOrderId },
    { title: 'Zoe Seller ID', value: orderDetails.ZoeSellerId },
    {
      title: 'Zoe Purchaser Account ID',
      value: orderDetails.ZoePurchaserAccountId
    },
    { title: 'Zoe Order Status ID', value: orderDetails.ZoeOrderStatusId },
    { title: 'Message ID', value: orderDetails.MessageId },

    // RMS Information
    { title: 'RMS Purchase Order ID', value: orderDetails.RmsPurchaseOrderId },
    {
      title: 'RMS Purchase Order Number',
      value: orderDetails.RmsPurchaseOrderNumber
    },
    { title: 'RMS Supplier ID', value: orderDetails.RmsSupplierId },

    // Vendor Information
    { title: 'Vendor Code', value: orderDetails.VendorCode },
    { title: 'Vendor Name', value: orderDetails.VendorName },

    // Shipment Information
    { title: 'Shipment Status', value: orderDetails.ShipmentStatus },
    { title: 'Shipping Method', value: orderDetails.ShippingMethod },
    { title: 'Ship From Address', value: orderDetails.ShipFromAddress },
    { title: 'Ship To Address', value: orderDetails.ShipToAddress },

    // Email Information
    { title: 'From Email Address', value: orderDetails.FromEmailAddress },
    { title: 'To Email Address', value: orderDetails.ToEmailAddress },

    // Financial Information
    { title: 'Subtotal', value: formatCurrency(orderDetails.SubTotal) },
    {
      title: 'Shipping & Handling',
      value: formatCurrency(orderDetails.ShippingAndHandling)
    },
    { title: 'Sales Tax', value: formatCurrency(orderDetails.SalesTax) },
    {
      title: 'Discount Amount',
      value: formatCurrency(orderDetails.DiscountAmount)
    },
    {
      title: 'Gift Card Used',
      value: formatCurrency(orderDetails.GiftCardUsedAmount)
    },
    {
      title: 'Free Gift Card',
      value: formatCurrency(orderDetails.FreeGiftCardAmount)
    },
    { title: 'Total', value: formatCurrency(orderDetails.Total) },
    {
      title: 'Calculated Subtotal',
      value: formatCurrency(orderDetails.CalculatedSubTotal)
    },
    {
      title: 'Calculated Total',
      value: formatCurrency(orderDetails.CalculatedTotal)
    },

    // Status & Tax
    { title: 'Tax Status', value: orderDetails.TaxStatus },

    // Timestamps
    { title: 'Created Date', value: formatDateTime(orderDetails.CreatedDate) },
    { title: 'Last Updated', value: formatDateTime(orderDetails.LastUpdated) }
  ];

  const renderJsonTab = () => {
    if (!orderDetails.OrderInfo) {
      return (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No additional order information available
        </Typography>
      );
    }

    try {
      const jsonString =
        typeof orderDetails.OrderInfo === 'string'
          ? orderDetails.OrderInfo
          : JSON.stringify(orderDetails.OrderInfo, null, 2);

      return (
        <Box sx={{ p: 2 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: '#1E2235',
              maxHeight: '600px',
              overflow: 'auto'
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: '#E2E8F0'
              }}
            >
              {jsonString}
            </pre>
          </Paper>
        </Box>
      );
    } catch (error) {
      return (
        <Typography color="error" sx={{ p: 2 }}>
          Error displaying order information
        </Typography>
      );
    }
  };

  const renderUpdateHistoryTab = () => {
    if (!orderDetails.UpdateHistory) {
      return (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No update history available
        </Typography>
      );
    }

    try {
      const jsonString =
        typeof orderDetails.UpdateHistory === 'string'
          ? orderDetails.UpdateHistory
          : JSON.stringify(orderDetails.UpdateHistory, null, 2);

      return (
        <Box sx={{ p: 2 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: '#1E2235',
              maxHeight: '600px',
              overflow: 'auto'
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: '#E2E8F0'
              }}
            >
              {jsonString}
            </pre>
          </Paper>
        </Box>
      );
    } catch (error) {
      return (
        <Typography color="error" sx={{ p: 2 }}>
          Error displaying update history
        </Typography>
      );
    }
  };

  return (
    <div>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: '#0F1117',
          minHeight: '100vh'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={() => navigate('/zoe-orders')}
            sx={{ mr: 2 }}
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Zoe Order Details
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
              sx={{ backgroundColor: 'white', flex: 1 }}
            >
              <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
              <Tab
                label="Purchase Order"
                icon={<ShoppingCartIcon />}
                iconPosition="start"
              />
              <Tab
                label="Order Info"
                icon={<ReceiptIcon />}
                iconPosition="start"
              />
              <Tab
                label="Update History"
                icon={<HistoryIcon />}
                iconPosition="start"
              />
            </Tabs>

            {/* Edit Button */}
            {activeTab === 0 && (
              <IconButton
                onClick={handleOpenEdit}
                sx={{ mr: 2 }}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
            {activeTab === 0 && (
              <PackageDetailsBasicInformationTab
                id={id}
                overviewItems={overviewItems}
                collapsibleItems={[]}
              />
            )}
            {activeTab === 1 && <OrderInfo zoeOrderId={id} />}
            {activeTab === 2 && renderJsonTab()}
            {activeTab === 3 && renderUpdateHistoryTab()}
          </Box>
        </Paper>
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Zoe Order Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.keys(formValues)
              .filter((key) => !['OrderInfo', 'UpdateHistory'].includes(key))
              .map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    label={key}
                    name={key}
                    value={formValues[key] ?? ''}
                    onChange={handleInputChange}
                  />
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
